// src/utils/api.js
import { API_BASE } from '../firebase/config';

async function apiFetch(path, options = {}, getToken) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {})
    }
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw Object.assign(new Error(err.error || 'Request failed'), { code: err.code, status: res.status, data: err });   // data: the whole answer (3.77.0: a 409 carries the latest note)
  }
  return res;
}

// For the sign-in screens, before there is a token
async function publicPost(path, body) {
  const res  = await fetch(`${API_BASE}${path}`, {
    method : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body   : JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(json.error || 'Something went wrong. Please try again.'), { code: json.code, status: res.status, until: json.until || null, block: json.code === 'IP_BLOCKED' ? { kind: json.kind, from: json.from, until: json.until } : null });
  }
  return json;
}

// ── Two-factor authentication ────────────────────────────────
export async function fetchMfaStatus(getToken) {
  const res = await apiFetch('/portal/mfa/status', {}, getToken);
  return res.json();
}

// A new set of recovery codes (shown once). Replacing codes that are
// still usable needs a recent sign-in: REAUTH_REQUIRED otherwise.
export async function createRecoveryCodes(getToken) {
  const res = await apiFetch('/portal/mfa/recovery-codes', { method: 'POST' }, getToken);
  return res.json();
}

// "Lost access to your authenticator app?" on the sign-in code screen.
// proof repeats the first sign-in step: { method: 'password', email,
// password } or { method: 'google', idToken }.
export const lostUseRecoveryCode = (proof, code) => publicPost('/portal/mfa/lost/recovery-code', { proof, code });
export const lostEmailResetLink  = proof => publicPost('/portal/mfa/lost/email-link', { proof });
export const lostApprovers       = proof => publicPost('/portal/mfa/lost/approvers', { proof });
export const lostAskAdmin        = (proof, to) => publicPost('/portal/mfa/lost/ask-admin', { proof, to });
export const checkResetLink      = token => publicPost('/portal/mfa/reset-link/check', { token });
export const confirmResetLink    = token => publicPost('/portal/mfa/reset-link/confirm', { token });
// Secondary email: the address to sign in with (main email for a secondary one), and the confirm link
export const signInEmailFor      = email => publicPost('/portal/access/sign-in-email', { email });
export const confirmSecondaryEmail = token => publicPost('/portal/access/confirm-email', { token });
// Remembered devices ("Don't ask for a code on this device for 30 days")
export const rememberedSignIn    = (proof, deviceToken) => publicPost('/portal/mfa/remember/sign-in', { proof, deviceToken });
export async function fetchRememberAvailable() {
  const res = await fetch(`${API_BASE}/portal/mfa/remember/available`);
  return res.ok ? res.json() : { available: false };
}
export async function rememberThisDevice(label, getToken) {
  const res = await apiFetch('/portal/mfa/remember', { method: 'POST', body: JSON.stringify({ label }) }, getToken);
  return res.json();
}
export async function forgetRememberedDevices(id, getToken) {
  const res = await apiFetch(`/portal/mfa/remember${id ? `/${encodeURIComponent(id)}` : ''}`, { method: 'DELETE' }, getToken);
  return res.json();
}

// ── User Management (administrators) ─────────────────────────
export async function fetchUsers(getToken) {
  const res = await apiFetch('/portal/users', {}, getToken);
  return res.json();
}

// Send a new invite: method 'link' | 'temp' | 'google'
export async function sendUserInvite(email, method, getToken) {
  const res = await apiFetch(`/portal/users/${encodeURIComponent(email)}/invite`, { method: 'POST', body: JSON.stringify({ method }) }, getToken);
  return res.json();
}

// Admin: method 'link' (reset email) | 'temp' (returns { password, expiresAt })
export async function resetUserPassword(email, method, getToken) {
  const res = await apiFetch(`/portal/users/${encodeURIComponent(email)}/password`, { method: 'POST', body: JSON.stringify({ method }) }, getToken);
  return res.json();
}

// After replacing a temporary password
export async function confirmPasswordChanged(getToken) {
  const res = await apiFetch('/portal/me/password-changed', { method: 'POST' }, getToken);
  return res.json();
}

export async function addUser(person, getToken) {
  const res = await apiFetch('/portal/users', { method: 'POST', body: JSON.stringify(person) }, getToken);
  return res.json();
}

// action: 'reset-2fa' | 'remove' | 'restore'
// body.account: which account (Super Admins on "All accounts", people in several accounts)
export async function userAction(email, action, getToken, body = {}) {
  const res = await apiFetch(`/portal/users/${encodeURIComponent(email)}/${action}`, { method: 'POST', body: JSON.stringify(body) }, getToken);
  return res.json();
}

// Multi-select filters hold arrays — serialize them comma-separated
export function filterParams(filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    const val = Array.isArray(v) ? v.join(',') : v;
    if (val) params.append(k, val);
  });
  return params;
}

// Your own profile (name, phone) and Settings, saved on the server
// 3.57.7: the person's own fax that failed again within the last hour (or null)
export async function fetchFailedAgain(getToken) {
  const res = await apiFetch('/portal/failed-again', {}, getToken);
  return (await res.json()).event || null;
}

// Fax pop-ups on every page (3.24.0): fax results since `since` (ISO) that
// this person can see. Without `since` it only returns the server time.
export async function fetchFaxEvents(since, getToken) {
  const q = since ? `?since=${encodeURIComponent(since)}` : '';
  const res = await apiFetch(`/portal/fax-events${q}`, {}, getToken);
  return res.json();
}

export async function fetchMe(getToken) {
  const res = await apiFetch('/portal/me', {}, getToken);
  return res.json();
}

export async function updateMe(changes, getToken) {
  const res = await apiFetch('/portal/me', { method: 'PUT', body: JSON.stringify(changes) }, getToken);
  return res.json();
}

export async function fetchOutgoing(filters, getToken) {
  const res = await apiFetch(`/portal/outgoing?${filterParams(filters)}`, {}, getToken);
  return res.json();
}

// Current status of specific outgoing faxes, for live list updates
export async function fetchOutgoingStatus(faxIds, getToken) {
  const res = await apiFetch('/portal/outgoing/status', {
    method: 'POST',
    body: JSON.stringify({ faxIds })
  }, getToken);
  return res.json();
}

// Incoming faxes arriving right now (for "Receiving…" rows)
export async function fetchIncomingInProgress(getToken) {
  const res  = await apiFetch('/portal/incoming/in-progress', {}, getToken);
  const json = await res.json();
  return json.data || [];
}

export async function fetchIncoming(filters, getToken) {
  const res = await apiFetch(`/portal/incoming?${filterParams(filters)}`, {}, getToken);
  return res.json();
}

export async function fetchSuggestions(type, field, q, getToken) {
  const params = new URLSearchParams({ type, field, q, limit: '50' });   // the list scrolls (3.14.0)
  const res  = await apiFetch(`/portal/suggest?${params}`, {}, getToken);
  const json = await res.json();
  return json.data || [];
}

// Download streams directly from backend — no signed URL needed
export async function getDownloadUrl(type, faxId, getToken) {
  const token = await getToken();
  const res   = await fetch(`${API_BASE}/portal/download/${type}/${faxId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Download failed' }));
    throw new Error(err.error || 'Download failed');
  }
  // Return blob URL for download
  const blob        = await res.blob();
  const url         = URL.createObjectURL(blob);
  // Try to get filename from Content-Disposition header
  const disposition = res.headers.get('Content-Disposition') || '';
  const match       = disposition.match(/filename="?([^"]+)"?/);
  const fileName    = match ? match[1] : `fax-${faxId}.pdf`;
  return { url, fileName };
}

// ── Secondary email (My profile) ─────────────────────────────
export async function addSecondaryEmail(email, getToken) {
  const res = await apiFetch('/portal/me/secondary-email', { method: 'POST', body: JSON.stringify({ email }) }, getToken);
  return res.json();
}
// pendingOnly: cancel an address still waiting for its confirm link
export async function removeSecondaryEmail(getToken, pendingOnly = false) {
  const res = await apiFetch(`/portal/me/secondary-email${pendingOnly ? '?pending=1' : ''}`, { method: 'DELETE' }, getToken);
  return res.json();
}

// Users → Reminders: src/pages/Reminders.jsx calls /portal/reminders itself (3.9)

// ── Online now / people report (User Management) ─────────────
// Sign people out on every device
export async function signOutUsers(emails, getToken) {
  const res = await apiFetch('/portal/users/sign-out', { method: 'POST', body: JSON.stringify({ emails }) }, getToken);
  return res.json();
}
// People report (.xlsx): the people picked, or null for the whole list.
// Saves the file in the browser.
export async function downloadPeopleReport(emails, getToken) {
  const res = await apiFetch('/portal/users/report', { method: 'POST', body: JSON.stringify({ emails }) }, getToken);
  const blob = await res.blob();
  const match = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = match ? match[1] : 'People.xlsx';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Fetch a stored fax file as a Blob. inline=true asks the backend to serve
// it for in-browser preview rather than as a download.
export async function fetchFaxFile(type, faxId, getToken, { inline = false } = {}) {
  const token = await getToken();
  const qs    = inline ? '?inline=1' : '';
  const res   = await fetch(`${API_BASE}/portal/download/${type}/${encodeURIComponent(faxId)}${qs}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Could not load the file' }));
    throw new Error(err.error || 'Could not load the file');
  }
  return res.blob();
}

// One outgoing fax with its full attempt history (original send, resends,
// auto-retries) and any scheduled retry
export async function fetchOutgoingDetail(faxId, getToken) {
  const res = await apiFetch(`/portal/fax/outgoing/${encodeURIComponent(faxId)}`, {}, getToken);
  return res.json();
}

// Cancel the automatic retry scheduled for a failed outgoing fax
// 3.69.4 (owner): a fax waiting in line goes out now (Admins and Managers)
export async function sendWaitingNow(queueId, getToken) {
  const res = await apiFetch(`/portal/outgoing/waiting/${encodeURIComponent(queueId)}/send-now`, { method: 'POST' }, getToken);
  return res.json();
}

export async function cancelScheduledRetry(faxId, getToken) {
  const res = await apiFetch(`/portal/fax/outgoing/${encodeURIComponent(faxId)}/cancel-retry`, { method: 'POST' }, getToken);
  return res.json();
}

// Several fax files as one zip (3.34.6): items [{ type, faxId }] → saves
// "faxes-YYYY-MM-DD.zip"; throws with the server's message on failure
export async function downloadZip(items, getToken) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/portal/download/zip`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ items }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Download failed'); }
  const blob = await res.blob();
  const name = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'faxes.zip';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export async function getBulkDownloadUrls(type, faxIds, getToken) {
  const res = await apiFetch('/portal/download/bulk', {
    method: 'POST',
    body: JSON.stringify({ faxIds, type })
  }, getToken);
  return res.json();
}

export async function downloadReport(type, filters, getToken) {
  const token = await getToken();
  const res = await fetch(`${API_BASE}/portal/report/${type}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(filters)
  });
  if (!res.ok) throw new Error('Report download failed');
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${type}-fax-report-${Date.now()}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
// ── Roles & Access (Super Admins) ────────────────────────────
export async function fetchRoles(getToken) {
  const res = await apiFetch('/portal/roles', {}, getToken);
  return res.json();
}
export async function updateRole(groupId, roleId, changes, getToken) {
  const res = await apiFetch(`/portal/roles/${encodeURIComponent(groupId)}/${encodeURIComponent(roleId)}`, { method: 'PUT', body: JSON.stringify(changes) }, getToken);
  return res.json();
}
export async function createRole(groupId, role, getToken) {
  const res = await apiFetch(`/portal/roles/${encodeURIComponent(groupId)}`, { method: 'POST', body: JSON.stringify(role) }, getToken);
  return res.json();
}
export async function deleteRole(groupId, roleId, getToken) {
  const res = await apiFetch(`/portal/roles/${encodeURIComponent(groupId)}/${encodeURIComponent(roleId)}`, { method: 'DELETE' }, getToken);
  return res.json();
}
export async function copyRoles(groupId, fromGroupId, getToken) {
  const res = await apiFetch(`/portal/roles/${encodeURIComponent(groupId)}/copy`, { method: 'POST', body: JSON.stringify({ from: fromGroupId }) }, getToken);
  return res.json();
}
export async function resetRole(groupId, roleId, getToken) {
  const res = await apiFetch(`/portal/roles/${encodeURIComponent(groupId)}/${encodeURIComponent(roleId)}/defaults`, { method: 'POST' }, getToken);
  return res.json();
}

// ── More User Management ─────────────────────────────────────
export async function setUserRole(email, role, getToken, account = null) {
  const res = await apiFetch(`/portal/users/${encodeURIComponent(email)}/role`, { method: 'PUT', body: JSON.stringify({ role, account }) }, getToken);
  return res.json();
}
// options (approve): { role, method }
export async function decideAccessRequest(email, decision, getToken, options = {}) {
  const res = await apiFetch(`/portal/users/requests/${encodeURIComponent(email)}/${decision}`, { method: 'POST', body: JSON.stringify(options) }, getToken);
  return res.json();
}
export async function fetchUnregisteredSenders(getToken) {
  const res = await apiFetch('/portal/users/unregistered-senders', {}, getToken);
  return res.json();
}
export async function fetchDigestRecipients(getToken) {
  const res = await apiFetch('/portal/users/unregistered-senders/recipients', {}, getToken);
  return res.json();
}
// Email the reminder (with "Request access") to the senders themselves.
// emails: some people, or null for everyone on the list who is due one
export async function remindUnregisteredSenders(emails, getToken) {
  const res = await apiFetch('/portal/users/unregistered-senders/remind', { method: 'POST', body: JSON.stringify({ emails }) }, getToken);
  return res.json();
}
export async function notifyUnregisteredSenders(to, getToken) {
  const res = await apiFetch('/portal/users/unregistered-senders/notify', { method: 'POST', body: JSON.stringify({ to }) }, getToken);
  return res.json();
}
// Follow-up owners (Super Admins): per group, the Admins who get the
// people-without-an-account list at once, every Monday, and at sign-in
export async function fetchFollowupOwners(getToken) {
  const res = await apiFetch('/portal/users/unregistered-senders/owners', {}, getToken);
  return res.json();
}
export async function setFollowupOwners(groupId, owners, getToken) {
  const res = await apiFetch('/portal/users/unregistered-senders/owners', { method: 'PUT', body: JSON.stringify({ groupId, owners }) }, getToken);
  return res.json();
}
export async function setEmailToFaxDeadline(deadline, getToken) {
  const res = await apiFetch('/portal/users/email-to-fax-settings', { method: 'PUT', body: JSON.stringify({ deadline }) }, getToken);
  return res.json();
}
// A tab (or section, 'users/activity') was opened: Activity log. Never throws.
export function logTabOpened(tab, getToken) {
  apiFetch('/portal/session/activity', { method: 'POST', body: JSON.stringify({ tab }) }, getToken).catch(() => {});
}

// params: { kind: 'changes' | 'activity', email, from, to (YYYY-MM-DD), before }
export async function fetchActivity(getToken, params = {}) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
  const res = await apiFetch(`/portal/users/activity${qs ? `?${qs}` : ''}`, {}, getToken);
  return res.json();
}

// ── Delete fax / Purged Faxes ────────────────────────────────
export async function deleteFax(type, faxId, getToken) {
  const res = await apiFetch(`/portal/fax/${type}/${encodeURIComponent(faxId)}/delete`, { method: 'POST' }, getToken);
  return res.json();
}
export async function restoreFax(type, faxId, getToken) {
  const res = await apiFetch(`/portal/fax/${type}/${encodeURIComponent(faxId)}/restore`, { method: 'POST' }, getToken);
  return res.json();
}
export async function fetchPurged(getToken) {
  const res = await apiFetch('/portal/purged', {}, getToken);
  return res.json();
}

// ── "Request access" link from the email-to-fax warning ──────
export const checkAccessLink = token => publicPost('/portal/access/check', { token });
export const requestAccess   = (token, name) => publicPost('/portal/access/request', { token, name });

// "Set my password" link from the welcome email
export const checkSetPasswordLink = token => publicPost('/portal/access/set-password/check', { token });
export const setPasswordFromLink  = (token, password) => publicPost('/portal/access/set-password', { token, password });

// ── Billing (Control Panel → Billing, Super Admins) ──────────
export async function fetchBilling(getToken) {
  const res = await apiFetch('/portal/billing', {}, getToken);
  return res.json();
}
// 3.67.3: cycleOverlap = the end day is the next start day, counted in both periods
export async function saveBilling(accountId, billing, getToken, cycleDay, cycleOverlap) {
  const res = await apiFetch(`/portal/billing/${encodeURIComponent(accountId)}`, { method: 'PUT', body: JSON.stringify({ billing, ...(cycleDay != null ? { cycleDay, cycleOverlap: !!cycleOverlap } : {}) }) }, getToken);
  return res.json();
}
// What a new billing cycle start day would do (3.13.0): { effective, preview }
export async function fetchCyclePreview(accountId, day, getToken, overlap = false) {
  const res = await apiFetch(`/portal/billing/${encodeURIComponent(accountId)}/cycle-preview?day=${encodeURIComponent(day)}&overlap=${overlap ? 1 : 0}`, {}, getToken);
  return res.json();
}

// ── Download what a page shows as Excel (3.16.0) ─────────────
// columns: [{ header, get: row => value, width? }]; the backend just builds
// the file from these rows (POST /portal/export)
export async function downloadSheet(title, columns, rows, getToken) {
  const res = await apiFetch('/portal/export', {
    method: 'POST',
    body: JSON.stringify({
      title,
      columns: columns.map(c => ({ header: c.header, width: c.width })),
      rows: rows.map(r => columns.map(c => { const v = c.get(r); return v == null ? '' : v; })),
    }),
  }, getToken);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Download failed');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
  URL.revokeObjectURL(url);
}

// Inbox count and Outbox failed dot (3.22.1)
export async function fetchBadges(getToken) {
  const res = await apiFetch('/portal/badges', {}, getToken);
  return res.json();
}
export async function markBoxSeen(box, getToken) {
  await apiFetch('/portal/badges/seen', { method: 'POST', body: JSON.stringify({ box }) }, getToken);
}

// Profile picture (3.22.2): { image } | { icon } | null
export async function saveAvatar(avatar, getToken) {
  const res = await apiFetch('/portal/me/avatar', { method: 'PUT', body: JSON.stringify({ avatar }) }, getToken);
  return res.json();
}

// ── My notes and Feedback (3.37.0, the menu under your name) ──
// The server always answers as the real signed-in person (View as ignored)
const jsonOf = res => res.json();
export const fetchNotes   = getToken => apiFetch('/portal/notes', {}, getToken).then(jsonOf);
export const addNote      = (note, getToken) => apiFetch('/portal/notes', { method: 'POST', body: JSON.stringify(note) }, getToken).then(jsonOf);
export const saveNote     = (id, note, getToken) => apiFetch(`/portal/notes/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(note) }, getToken).then(jsonOf);
export const deleteNote   = (id, getToken) => apiFetch(`/portal/notes/${encodeURIComponent(id)}`, { method: 'DELETE' }, getToken).then(jsonOf);
// 3.74.0: Recently deleted (kept 30 days), Restore, Delete forever
export const fetchDeletedNotes = getToken => apiFetch('/portal/notes/deleted', {}, getToken).then(jsonOf);
export const restoreNote  = (id, getToken) => apiFetch(`/portal/notes/${encodeURIComponent(id)}/restore`, { method: 'POST' }, getToken).then(jsonOf);
export const fetchNextReminder = getToken => apiFetch('/portal/notes/next-reminder', {}, getToken).then(jsonOf);   // 3.74.2
// 3.76.0: Duplicate / new from a template, a fax added to a note ('new' = a new note), a linked fax's row
export const copyNote     = (id, fromTemplate, getToken) => apiFetch(`/portal/notes/${encodeURIComponent(id)}/copy`, { method: 'POST', body: JSON.stringify({ fromTemplate: !!fromTemplate }) }, getToken).then(jsonOf);
export const addFaxToNote = (id, link, getToken) => apiFetch(`/portal/notes/${encodeURIComponent(id)}/links`, { method: 'POST', body: JSON.stringify({ link }) }, getToken).then(jsonOf);
export const fetchFaxRow  = (type, id, getToken) => apiFetch(`/portal/fax-row?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`, {}, getToken).then(jsonOf);
// 3.77.0: sharing a note
export const fetchSharePeople = getToken => apiFetch('/portal/notes/share-people', {}, getToken).then(jsonOf);
export const saveNoteShares = (id, shares, getToken) => apiFetch(`/portal/notes/${encodeURIComponent(id)}/shares`, { method: 'PUT', body: JSON.stringify({ shares }) }, getToken).then(jsonOf);
export const leaveSharedNote = (id, getToken) => apiFetch(`/portal/notes/${encodeURIComponent(id)}/shares/me`, { method: 'DELETE' }, getToken).then(jsonOf);
export const deleteNoteForever = (id, getToken) => apiFetch(`/portal/notes/${encodeURIComponent(id)}/forever`, { method: 'DELETE' }, getToken).then(jsonOf);
export const sendFeedback = (fb, getToken) => apiFetch('/portal/feedback', { method: 'POST', body: JSON.stringify(fb) }, getToken).then(jsonOf);
export const fetchFeedback = getToken => apiFetch('/portal/feedback', {}, getToken).then(jsonOf);
export const fetchFeedbackCount = getToken => apiFetch('/portal/feedback/count', {}, getToken).then(jsonOf);
export const setFeedbackStatus = (id, status, getToken) => apiFetch(`/portal/feedback/${encodeURIComponent(id)}/status`, { method: 'POST', body: JSON.stringify({ status }) }, getToken).then(jsonOf);
export const replyToFeedback = (id, message, getToken) => apiFetch(`/portal/feedback/${encodeURIComponent(id)}/reply`, { method: 'POST', body: JSON.stringify({ message }) }, getToken).then(jsonOf);
// Several at once (3.38.0): action = seen | done | reopen | archive | unarchive | delete
export const bulkFeedback = (ids, action, getToken) => apiFetch('/portal/feedback/bulk', { method: 'POST', body: JSON.stringify({ ids, action }) }, getToken).then(jsonOf);

// Notification bell (3.56.0): { items, count }; clear one ({ key }) or all ({ all: true })
export async function fetchBell(getToken) {
  const res = await apiFetch('/portal/bell', {}, getToken);
  return res.json();
}
export async function clearBell(body, getToken) {
  await apiFetch('/portal/bell/clear', { method: 'POST', body: JSON.stringify(body) }, getToken);
}
// 3.59.2: a fax or people action's pop-up, kept in the bell (listed, not counted)
export async function addBellAlert(alert, getToken) {
  await apiFetch('/portal/bell/alert', { method: 'POST', body: JSON.stringify(alert) }, getToken);
}

// 3.70.0: the live steps of faxes just sent from Compose (by `ref`)
export async function getSendProgress(refs, getToken) {
  const res = await apiFetch(`/portal/send-progress?refs=${refs.map(encodeURIComponent).join(',')}`, {}, getToken);
  return (await res.json()).data || [];
}
// 3.75.0: Settings → Fax Settings → Sending Timings (one set for all clients; saving is Super Admins only)
export const fetchSendTimings = getToken => apiFetch('/portal/send-timings', {}, getToken).then(jsonOf);
export const saveSendTimings = (values, getToken) => apiFetch('/portal/send-timings', { method: 'PUT', body: JSON.stringify({ values }) }, getToken).then(jsonOf);
